import { useState, type FormEvent } from 'react';
import { Decimal, validerTranches } from '@gsm/partage';
import { useRessource, messageErreur } from '../../api/ressource';
import { api, ErreurApi } from '../../api/client';
import { useAuth } from '../../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../../composants/Tableau';
import { BandeauAlerte } from '../../composants/BandeauAlerte';
import { Champ } from '../../composants/Champ';
import { Pastille } from '../../composants/Pastille';
import { Modale } from '../../composants/Modale';
import { Icone } from '../../composants/Icone';

interface Tranche {
  id: string;
  code: string;
  libelle: string;
  borneInfAns: string;
  borneSupAns: string;
  ordre: number;
  actif: boolean;
}

interface Diagnostic {
  tranches: { code: string; libelle: string }[];
  anomalies: { code: string; message: string }[];
}

const VIDE = { code: '', libelle: '', borneInfAns: '', borneSupAns: '', ordre: '', actif: true };

/** Borne en années, décimale à la française : « 0,5 », jamais « 0.5 ». */
const annees = (v: string) => new Decimal(v).toString().replace('.', ',');

export function Tranches() {
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Tranche[]>('/tranches-maturite');
  const diagnostic = useRessource<Diagnostic>('/tranches-maturite/diagnostic');
  const tranches = donnees ?? [];
  const anomalies = diagnostic.donnees?.anomalies ?? [];
  const peutCreer = aRole('ADMIN');

  const [edition, setEdition] = useState<string | null>(null);
  const [saisie, setSaisie] = useState(VIDE);
  const [erreurForm, setErreurForm] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const enCreation = edition === '';

  const champ = (cle: keyof typeof VIDE) => ({
    value: String(saisie[cle]),
    onChange: (e: { target: { value: string } }) => setSaisie({ ...saisie, [cle]: e.target.value }),
  });

  function ouvrirCreation() {
    setSaisie(VIDE);
    setEdition('');
    setErreurForm(null);
  }
  function ouvrirEdition(t: Tranche) {
    setSaisie({
      code: t.code,
      libelle: t.libelle,
      borneInfAns: String(Number(t.borneInfAns)),
      borneSupAns: String(Number(t.borneSupAns)),
      ordre: String(t.ordre),
      actif: t.actif,
    });
    setEdition(t.id);
    setErreurForm(null);
  }
  function fermer() {
    setEdition(null);
  }

  // Contrôle en direct : la tranche saisie est confrontée au découpage
  // existant (dont on retire la tranche éditée pour qu'elle ne se heurte pas
  // à elle-même), avec la même fonction que le serveur.
  const apercu =
    edition !== null && saisie.borneInfAns && saisie.borneSupAns
      ? validerTranches([
          ...tranches
            .filter((t) => t.actif && t.id !== edition)
            .map((t) => ({
              code: t.code,
              libelle: t.libelle,
              borneInfAns: Number(t.borneInfAns),
              borneSupAns: Number(t.borneSupAns),
              ordre: t.ordre,
            })),
          {
            code: saisie.code || 'NOUVELLE',
            libelle: saisie.libelle || 'Nouvelle tranche',
            borneInfAns: Number(saisie.borneInfAns),
            borneSupAns: Number(saisie.borneSupAns),
            ordre: Number(saisie.ordre) || 99,
          },
        ])
      : [];

  async function enregistrer(evenement: FormEvent) {
    evenement.preventDefault();
    setErreurForm(null);
    setOccupe(true);
    try {
      const corps = {
        code: saisie.code,
        libelle: saisie.libelle,
        borneInfAns: saisie.borneInfAns,
        borneSupAns: saisie.borneSupAns,
        ordre: Number(saisie.ordre),
      };
      if (enCreation) {
        await api.post('/tranches-maturite', corps);
      } else {
        await api.patch(`/tranches-maturite/${edition}`, { ...corps, actif: saisie.actif });
      }
      fermer();
      await Promise.all([recharger(), diagnostic.recharger()]);
    } catch (e) {
      setErreurForm(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  const colonnes: Colonne<Tranche>[] = [
    { cle: 'ordre', entete: 'Ordre', num: true, rendu: (t) => t.ordre },
    { cle: 'code', entete: 'Code', num: true, rendu: (t) => t.code },
    { cle: 'libelle', entete: 'Tranche', rendu: (t) => t.libelle },
    {
      cle: 'bornes',
      entete: 'Maturité résiduelle',
      num: true,
      rendu: (t) => `[${annees(t.borneInfAns)} ; ${annees(t.borneSupAns)}[`,
    },
    {
      cle: 'actif',
      entete: 'État',
      rendu: (t) =>
        t.actif ? (
          <Pastille ton="succes">Actif</Pastille>
        ) : (
          <Pastille ton="neutre">Inactif</Pastille>
        ),
    },
    ...(peutCreer
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (t: Tranche) => (
              <button
                type="button"
                className="tableau__action"
                onClick={() => ouvrirEdition(t)}
                aria-label={`Éditer ${t.libelle}`}
              >
                <Icone nom="editer" taille={16} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">Référentiels</p>
      <div className="entete">
        <h1>Tranches de maturité</h1>
        <span className="entete__ref">poche taux · BDT</span>
      </div>

      {erreur && (
        <BandeauAlerte nature="refus" titre="Chargement impossible">
          {messageErreur(erreur)}
        </BandeauAlerte>
      )}

      {anomalies.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Découpage incohérent">
          {anomalies.map((a) => a.message).join(' ')}
        </BandeauAlerte>
      )}

      <Bloc
        titre="Découpage"
        meta={chargement ? 'chargement…' : 'borne inférieure incluse, supérieure exclue'}
        actions={
          peutCreer && (
            <button
              type="button"
              className="bouton bouton--icone bouton--compact"
              onClick={ouvrirCreation}
            >
              <Icone nom="creer" taille={15} />
              Nouvelle tranche
            </button>
          )
        }
      >
        <Tableau colonnes={colonnes} lignes={tranches} cle={(t) => t.id} />
        <p className="note">
          La tranche d’une ligne BDT se déduit chaque jour de sa maturité résiduelle : en
          vieillissant, la ligne passe d’une tranche à l’autre.
        </p>
      </Bloc>

      <Modale
        ouverte={edition !== null}
        onFermer={fermer}
        surtitre="Tranche de maturité"
        titre={enCreation ? 'Nouvelle tranche' : 'Modifier la tranche'}
      >
        {erreurForm && (
          <BandeauAlerte nature="refus" titre="Enregistrement refusé">
            {erreurForm}
          </BandeauAlerte>
        )}
        {apercu.length > 0 && (
          <BandeauAlerte nature="gestion" titre="Le découpage ne serait plus cohérent">
            {apercu.map((a) => a.message).join(' ')}
          </BandeauAlerte>
        )}
        <form onSubmit={(e) => void enregistrer(e)} noValidate>
          <Champ label="Code" {...champ('code')} placeholder="T_2_5" required />
          <Champ label="Libellé" {...champ('libelle')} placeholder="2 à 5 ans" required />
          <Champ
            label="Borne inférieure (années, incluse)"
            montant
            {...champ('borneInfAns')}
            placeholder="2"
            required
          />
          <Champ
            label="Borne supérieure (années, exclue)"
            montant
            {...champ('borneSupAns')}
            placeholder="5"
            required
          />
          <Champ label="Ordre d’affichage" montant {...champ('ordre')} placeholder="3" required />
          {!enCreation && (
            <label className="case-a-cocher">
              <input
                type="checkbox"
                checked={saisie.actif}
                onChange={(e) => setSaisie({ ...saisie, actif: e.target.checked })}
              />
              Tranche active
            </label>
          )}
          <div className="modale__actions">
            <button type="button" className="bouton bouton--fantome" onClick={fermer}>
              Annuler
            </button>
            <button
              className="bouton"
              type="submit"
              disabled={
                occupe ||
                apercu.length > 0 ||
                !saisie.code ||
                !saisie.libelle ||
                !saisie.borneInfAns ||
                !saisie.borneSupAns ||
                !saisie.ordre
              }
            >
              {occupe ? 'Enregistrement…' : enCreation ? 'Créer la tranche' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modale>
    </>
  );
}
